import { Trans, useTranslation } from 'react-i18next';
import {
  backButton,
  hapticFeedbackImpactOccurred,
  miniApp,
  openInvoice,
  openLink
} from '@telegram-apps/sdk-react';
import type { InvoiceStatus } from '@telegram-apps/bridge';
import { CSSProperties, useEffect, useState } from 'react';

import {
  benefitsContainerStyle,
  benefitsHeaderStyle,
  benefitStyle,
  bottomGradientStyle,
  containerStyle,
  fixedFooterStyle,
  headerStyle,
  headlineStyle,
  scrollableContentStyle,
  selectorBlockStyle,
  selectorContainerStyle,
  subTitleStyle
} from './styles.ts';

import CheckShape from '@/components/common/svg/shapes/CheckShape.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import PurchaseItem from '@/components/Popup/PaywallPopup/components/PurchaseItem.tsx';
import PurchaseController from '@/api/purchases/purchases.ts';
import { paywallConfig } from '@/components/Popup/PaywallPopup/config.tsx';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar.tsx';
import FallingStarShape from '@/components/common/svg/shapes/FallingStarShape.tsx';
import SmallStarsShape from '@/components/common/svg/shapes/SmallStarsShape.tsx';
import { useHeaderStore } from '@/store/useHeadersStylesStore.ts';
import UIConfig from '@/UIConfig.ts';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';

type PaywallPopupProps = {
  handleClose: () => void;
  callAfterPaywallAction?: () => Promise<unknown>;
  type: PaywallTypes;
  placement: ProductInvoicePlacements;
  hideBackButton?: boolean;
  prevHeaderTemplate?: 'dark' | 'light';
};

const PremiumCheckbox = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: isSelected ? '#FFF' : 'transparent',
        border: '2px solid #FFF',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {isSelected && (
        <CheckShape width={14} height={14} strokeColor="#000" strokeWidth={3} />
      )}
    </div>
  );
};

/**
 * `callAfterPaywallAction` - Optional async callback invoked after successful payment. If defined, it overrides the default `handleClose`.
 */
const PaywallPopup = ({
  handleClose,
  type,
  placement,
  callAfterPaywallAction,
  hideBackButton = true,
  prevHeaderTemplate = 'dark'
}: PaywallPopupProps) => {
  useBackButtonToCloseModal(handleClose, hideBackButton);

  const { t } = useTranslation();
  const { aboutMe } = useProfileStore();
  const [appBottomBarColor] = useState(miniApp.bottomBarColor());

  const { setHeaderColors, switchHeaderColors } = useHeaderStore();

  const paywalls = aboutMe?.billing_offerings?.find(
    (el) => el.id === paywallConfig[type].id
  );

  const maxDiscount = paywalls?.package_deals?.reduce(
    (max, p) => Math.max(max, p.price_saving_percentage || 0),
    0
  );

  const defaultPackage =
    paywalls?.package_deals?.find(
      (p) => p.price_saving_percentage === maxDiscount
    ) || paywalls?.package_deals?.[0];

  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(
    defaultPackage?.id ?? null
  );
  const [selectedPrice, setSelectedPrice] = useState<number>(
    defaultPackage?.amount ?? 0
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const createInvoice = async () => {
    if (!selectedPackageId || isProcessing) return;
    setIsProcessing(true);
    try {
      const res = await PurchaseController.createInvoice({
        package_deal_id: selectedPackageId,
        product_placement_name: placement
      });
      const status: InvoiceStatus = await openInvoice(res.invoice_url, 'url');
      if (status === 'paid') {
        if (hideBackButton && backButton.isVisible()) {
          backButton.hide();
        }

        if (callAfterPaywallAction) {
          await callAfterPaywallAction();
        } else {
          handleClose();
        }
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const previousBgColor = miniApp.backgroundColor();
    const previousHeaderColor = miniApp.headerColor();

    miniApp.setBackgroundColor('#1D1D1D');
    miniApp.setHeaderColor('#1D1D1D');
    miniApp.setBottomBarColor('#1D1D1D');

    setHeaderColors({
      backgroundColor: UIConfig.Colors.BackgroundDark,
      logoColor: UIConfig.Colors.BackgroundWhite
    });

    return () => {
      miniApp.setBackgroundColor(previousBgColor);
      miniApp.setHeaderColor(previousHeaderColor);
      miniApp.setBottomBarColor(appBottomBarColor);

      if (prevHeaderTemplate === 'light') {
        switchHeaderColors('light');
      }

      if (prevHeaderTemplate === 'dark') {
        switchHeaderColors('dark');
      }
    };
  }, []);

  return (
    <div style={containerStyle}>
      <div style={scrollableContentStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1 style={headlineStyle}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      backgroundColor: '#FFF',
                      opacity: 0.03
                    }}
                  />

                  <div
                    style={{
                      position: 'absolute',
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: paywallConfig[type].mainIconGradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {paywallConfig[type].mainIcon}
                  </div>

                  <div style={{ position: 'absolute', top: -25, left: -30 }}>
                    <FallingStarShape height={40} width={40} />
                  </div>
                  <div style={{ position: 'absolute', bottom: -5, right: -10 }}>
                    <SmallStarsShape height={28} width={17} />
                  </div>
                </div>

                <span
                  style={{
                    marginTop: '0px',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '30px',
                    textAlign: 'center'
                  }}
                >
                  {t(paywallConfig[type].headerText)}
                </span>
              </div>
            </h1>
          </div>
          <p style={subTitleStyle}>
            <Trans
              i18nKey={paywallConfig[type].subtitleText}
              components={{ br: <br />, bold: <b /> }}
            />
          </p>
        </div>

        <div style={selectorContainerStyle}>
          {paywalls?.package_deals?.map((p) => (
            <div
              key={p.id}
              style={{
                width: '100%',
                marginBottom: '10px'
              }}
            >
              <div
                style={{
                  ...selectorBlockStyle,
                  border:
                    selectedPackageId === p.id
                      ? '2px solid #fff'
                      : `2px solid ${UIConfig.Colors.BackgroundDark}`
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    cursor: 'pointer',
                    width: '100%',
                    height: 62,
                    padding: '0 12px',
                    boxSizing: 'border-box'
                  }}
                  onClick={() => {
                    setSelectedPackageId(p.id);
                    setSelectedPrice(p.amount);
                    hapticFeedbackImpactOccurred('rigid');
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      marginRight: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PremiumCheckbox
                        isSelected={selectedPackageId === p.id}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      flexGrow: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <PurchaseItem
                      price={p.amount}
                      discountValue={p.price_saving_percentage}
                      icon={paywallConfig[type].icon({
                        isPreSelected: selectedPackageId === p.id,
                        description: paywallConfig[type].getDescription(p, t)
                      })}
                      isBestDeal={p.price_saving_percentage === maxDiscount}
                      style={{
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        background: 'transparent',
                        border: 'none',
                        margin: 0
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={benefitsContainerStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <span style={benefitsHeaderStyle}>
              {t(paywallConfig[type].benefits.header)}
            </span>
          </div>
          {paywallConfig[type].benefits.content.map((benefit, index) => (
            <div
              key={index}
              style={{
                ...benefitStyle,
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  marginTop: 3,
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  border: '2px solid #FDC93F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {benefit.before}
              </div>

              <div style={{ flexGrow: 1, marginLeft: 15 }}>
                <span style={{ fontWeight: 500 }}>
                  {t(benefit.description)}
                </span>
                {benefit.additionalText && (
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#CBC8C8',
                      marginTop: '4px',
                      lineHeight: '1.3'
                    }}
                  >
                    {t(benefit.additionalText)}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div
            style={{
              ...legalStyle,
              marginTop: 16,
              textAlign: 'left',
              width: '100%',
              paddingLeft: 0,
              paddingRight: 0
            }}
          >
            <Trans
              i18nKey="registration.consent_paywall"
              components={{
                br: <br />,
                terms: (
                  <span
                    onClick={() => {
                      openLink('https://kyivdate.app/terms/');
                    }}
                    style={legalLinkStyle}
                  />
                ),
                privacy: (
                  <span
                    onClick={() => {
                      openLink('https://kyivdate.app/privacy/');
                    }}
                    style={legalLinkStyle}
                  />
                )
              }}
            />
          </div>
        </div>
      </div>

      <div style={fixedFooterStyle}>
        <div style={bottomGradientStyle} />
        <MainButton
          text={
            <span>
              {t('paywall.pay_x_stars')}
              <TelegramCustomStar
                style={{
                  marginLeft: '4px',
                  marginRight: '4px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: '-2px'
                }}
                height={17}
                width={17}
                fillColor="#fff"
              />
              {selectedPrice}
            </span>
          }
          buttonStyle={{
            background: paywallConfig[type].mainIconGradient,
            color: '#fff'
          }}
          containerStyle={{ width: '90%' }}
          onClick={createInvoice}
          disabled={!selectedPackageId || isProcessing}
          loading={isProcessing}
        />
      </div>
    </div>
  );
};

const legalStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 510,
  color: 'rgba(255, 255, 255, 0.4)',
  textAlign: 'center',
  paddingLeft: '16px',
  paddingRight: '16px'
};

const legalLinkStyle: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.4)',
  textDecoration: 'underline'
};

export default PaywallPopup;
